/**
 * AppNav — top navigation bar with 5-section IA.
 * Primary: Home · Services · Report · Support (icons + text, bottom-border active)
 * Utility: persona switcher, AI assistant, notifications, Account dropdown
 * Hybrid design: 2026 bottom-border active pattern with icons for utility demographics.
 * Atlas: global shell.
 */
"use client";

import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import {
  Bell,
  Home,
  Sparkles,
  ClipboardList,
  AlertTriangle,
  MessageCircle,
  User,
  ChevronDown,
  Settings,
  CreditCard,
  Gauge,
} from "lucide-react";
import { useState, useRef, useCallback } from "react";
import DemoToolbar from "@/components/layout/DemoToolbar";
import LanguageSwitcher from "@/components/landing/LanguageSwitcher";
import { useActivePersona } from "@/lib/persona";
import { useT } from "@/lib/locale";
import { useDismissable, BRAND } from "@petci/shared-ui";

const AssistantPanel = dynamic(
  () => import("@/components/AssistantPanel").then((m) => m.AssistantPanel),
  { ssr: false },
);

const NAV_LINKS = [
  { href: "/home", labelKey: "nav.home", icon: Home },
  { href: "/services", labelKey: "nav.services", icon: ClipboardList },
  { href: "/report", labelKey: "nav.report", icon: AlertTriangle },
  { href: "/support", labelKey: "nav.support", icon: MessageCircle },
];

// `labelKey`, not `label` — matching NAV_LINKS above. These were raw strings, so
// the account menu stayed English in es/ht while the rest of the chrome switched.
// A `label:` inside a data array is invisible to `lint-i18n-literals` (it scans
// JSX TEXT NODES, and `{item.label}` is an expression), which is why this file
// being in the lint's include-set never caught it.
//
// Every key already existed and is already translated — zero additions to the
// frozen native-review pack. Four are byte-identical in en-TC; two changed, both
// deliberately (Godfred, 2026-07-30):
//   · notifications → the stub catalogue's own name, which is what MOBILE already
//     renders for this item, so the two surfaces finally agree (parity contract).
//   · /account → `account.title`, the h1 that page gives itself, per D-8's rule
//     that an affordance names the destination as the destination names itself.
const ACCOUNT_MENU_ITEMS = [
  { href: "/account/update", labelKey: "stubs.accountUpdate.name", icon: User },
  { href: "/account/notifications", labelKey: "stubs.notifications.name", icon: Bell },
  { href: "/account/autopay", labelKey: "stubs.autopay.name", icon: CreditCard },
  { href: "/account/meter-tools", labelKey: "stubs.meterTools.name", icon: Gauge },
  { href: "/account/feedback", labelKey: "stubs.feedback.name", icon: MessageCircle },
  { href: "/account", labelKey: "account.title", icon: Settings },
];

export function AppNav() {
  const pathname = usePathname();
  const persona = useActivePersona();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const t = useT();
  const accountRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);

  const notifications = NOTIF_SEEDS[persona.id] ?? NOTIF_SEEDS.P1 ?? [];
  const unreadCount = notifications.filter((item, i) => item.unread && !readIds.has(`${persona.id}-${i}`)).length;

  const closeAccount = useCallback(() => setAccountOpen(false), []);
  const closeNotif = useCallback(() => setNotifOpen(false), []);
  useDismissable({ open: accountOpen, onClose: closeAccount, ref: accountRef });
  useDismissable({ open: notifOpen, onClose: closeNotif, ref: notifRef });

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-neutral-200/60 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl 2xl:max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-6">
            {/* Brand */}
            <Link
              href="/home"
              className="flex items-center gap-2 flex-shrink-0"
              aria-label={t("brand.homeAria")}
            >
              {/* D-4: one mark, both surfaces. This was an inline blob glyph
                  with a hardcoded #C1774A — off-SSoT as well as off-brand. The
                  PNG is generated from the letterhead by
                  apps/mobile/scripts/generate-icons.mjs. */}
              {/* `priority`: next/image lazy-loads by default, which pops the
                  brand mark in after first paint on the highest-traffic chrome
                  in the app. It is above the fold on every route. */}
              <Image
                src="/pelican-mark.png"
                alt=""
                width={28}
                height={28}
                priority
                aria-hidden="true"
                className="flex-shrink-0"
              />
              {/* R-N4 (Godfred, 2026-07-30) — dated-copy-allow: a ruling date in a
                  code comment, not customer copy. `lint-dated-copy`'s comment-only
                  test recognises `//`, `*` and `/*` but not JSX's `{/*`, so this
                  file's new aggressive-tier enrollment reads the line as prose.
                  Backlogged as a lint gap rather than widened here.

                  The full form, not `short`.
                  The app chrome used to say "Pelican Energy" while the landing
                  said "Pelican Energy TCI" — defended as "already introduced, and
                  repetitive at every route", which is a taste argument. Against
                  it: the owner read the split as drift and a cold reviewer
                  comparing surfaces will too, "TCI" is the islands identity the
                  mission puts first, and ONE width-driven rule beats two
                  voice-driven ones. The policy is now full form wherever measured
                  width allows.

                  Two spans, not one: this wordmark was `hidden sm:inline`, so
                  below 640px web rendered NO name at all — the failure mobile's
                  D-4 already fixed, where the width picks WHICH form renders,
                  never WHETHER one does. Narrow web now gets `compact`. */}
              <span className="font-display font-bold text-neutral-900 text-sm hidden sm:inline">
                {BRAND.full}
              </span>
              <span className="font-display font-bold text-neutral-900 text-sm sm:hidden">
                {BRAND.compact}
              </span>
            </Link>

            {/* Primary nav — icons + text, bottom-border active.
                Delayed to `lg` (R2 combined-CHECK §2-R1): below `lg` the app is
                rail-only by design (no primary links, no hamburger — navigation
                flows through home cards + the Account menu), and the nav + full
                utility rail sum to ~843px, overflowing 768. 768–1023 inherits the
                designed rail-only pattern; the full nav fits from 1024.
                Precedent: PublicHeader also delays its nav to `lg`. */}
            <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map(({ href, labelKey, icon: Icon }) => {
                const isActive = pathname.startsWith(href);
                return (
                  <Link
                    key={href}
                    href={href}
                    aria-current={isActive ? "page" : undefined}
                    className={[
                      "flex items-center gap-1.5 h-16 px-3 text-sm font-medium border-b-2 transition-colors duration-fast",
                      isActive
                        ? "border-brand-secondary text-neutral-900"
                        : "border-transparent text-neutral-500 hover:text-neutral-900 hover:border-neutral-300",
                    ].join(" ")}
                  >
                    <Icon size={15} aria-hidden />
                    {t(labelKey)}
                  </Link>
                );
              })}
            </nav>

            {/* Utility rail */}
            <div className="flex items-center gap-2">
              {process.env.NEXT_PUBLIC_SHOW_LANG_SWITCHER !== "false" && (
                <LanguageSwitcher />
              )}

              <DemoToolbar />

              {/* AI assistant trigger — fill-active (different register from primary nav underline-active) */}
              <button
                onClick={() => setAssistantOpen((v) => !v)}
                aria-expanded={assistantOpen}
                aria-label="Open AI assistant"
                className={[
                  "p-1.5 rounded-lg transition-colors duration-fast",
                  assistantOpen
                    ? "bg-brand-primary-hover text-white"
                    : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100",
                ].join(" ")}
              >
                <Sparkles size={18} aria-hidden />
              </button>

              <div ref={notifRef} className="relative">
                <button
                  onClick={() => { setNotifOpen((v) => !v); setAccountOpen(false); }}
                  aria-label={`Notifications (${unreadCount} unread)`}
                  aria-expanded={notifOpen}
                  aria-haspopup="true"
                  className="relative p-1.5 rounded-lg text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors duration-fast"
                >
                  <Bell size={18} aria-hidden />
                  {unreadCount > 0 && (
                    <span
                      aria-hidden="true"
                      className="absolute top-0.5 right-0.5 w-4 h-4 bg-status-danger text-white text-[9px] font-bold rounded-full flex items-center justify-center"
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>

                {notifOpen && (
                  <NotificationDropdown
                    personaId={persona.id}
                    readIds={readIds}
                    onMarkAllRead={() => {
                      const ids = new Set(readIds);
                      notifications.forEach((_, i) => ids.add(`${persona.id}-${i}`));
                      setReadIds(ids);
                      setNotifOpen(false);
                    }}
                    onClose={() => setNotifOpen(false)}
                  />
                )}
              </div>

              {/* Account dropdown — fill-active (utility register, not route-state underline) */}
              <div ref={accountRef} className="relative">
                <button
                  onClick={() => setAccountOpen((v) => !v)}
                  aria-expanded={accountOpen}
                  aria-haspopup="true"
                  aria-label="Account menu"
                  className={[
                    "flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm font-medium transition-colors duration-fast",
                    accountOpen || pathname.startsWith("/account")
                      ? "bg-brand-primary-hover text-white"
                      : "text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100",
                  ].join(" ")}
                >
                  <User size={15} aria-hidden />
                  <span className="hidden lg:inline">{t("nav.account")}</span>
                  <ChevronDown
                    size={12}
                    aria-hidden
                    className={`transition-transform duration-fast ${accountOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {accountOpen && (
                  <div
                    className="absolute right-0 mt-2 w-64 rounded-lg border border-neutral-200 bg-white shadow-lg py-2 z-50 animate-fade-in"
                    role="menu"
                  >
                    <div className="px-4 py-2 border-b border-neutral-200">
                      <p className="text-sm font-semibold text-neutral-900">
                        {persona.contactName}
                      </p>
                      <p className="text-xs text-neutral-500">
                        #{persona.accountNumber}
                      </p>
                    </div>

                    {ACCOUNT_MENU_ITEMS.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          role="menuitem"
                          onClick={() => setAccountOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors duration-fast"
                        >
                          <Icon size={15} className="text-neutral-500" aria-hidden />
                          {t(item.labelKey)}
                        </Link>
                      );
                    })}

                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {assistantOpen && (
        <AssistantPanel onClose={() => setAssistantOpen(false)} />
      )}
    </>
  );
}

/**
 * D2-m fixture-prose tier — stays English on purpose. apps/mobile/components/AppHeader.tsx
 * carries the same English seeds, so this is CONSISTENT across surfaces, not a parity gap;
 * localizing web alone would create one. Named post-handover deferral (R-N1a, 2026-07-30):
 * these are synthetic event DATA quoting amounts, feeders and kWh — the exact class D2-m
 * defers — not the app's own chrome voice, which is why the account menu three lines above
 * WAS keyed and these are not.
 *
 * The duplication between the two apps is guarded, not just described: AppNav.seeds.test.ts
 * and apps/mobile/tests/notif-seeds-parity.test.ts each read BOTH files and assert the 12
 * bodies are deep-equal. D2-m's justification IS the consistency, so the consistency needs a
 * mechanical guard rather than a comment promising it.
 */
const NOTIF_SEEDS: Record<string, { icon: string; text: string; time: string; unread: boolean }[]> = {
  P1: [
    { icon: "⚡", text: "Outage resolved — Bight Park Feeder restored", time: "2h ago", unread: true },
    { icon: "📄", text: "Your latest bill is ready — $311.84", time: "1d ago", unread: true },
    { icon: "🌞", text: "Solar export credited: 142 kWh last month", time: "3d ago", unread: false },
  ],
  P2: [
    { icon: "📄", text: "Your latest bill is ready — $665.40", time: "1d ago", unread: true },
    { icon: "🔔", text: "High-usage alert: 15% above average this week", time: "2d ago", unread: true },
    { icon: "⚡", text: "Planned maintenance scheduled — Downtown Provo", time: "4d ago", unread: false },
  ],
  P3: [
    { icon: "⚠️", text: "Kitchen meter usage +18% anomaly detected", time: "6h ago", unread: true },
    { icon: "📄", text: "Your latest bill is ready — $73,402.86", time: "1d ago", unread: true },
    { icon: "⚡", text: "Demand near-miss: 412 kW / 450 kW threshold", time: "2d ago", unread: false },
  ],
  P4: [
    { icon: "⚠️", text: "Vacant anomaly — Villa Sea Breeze (32 kWh/day)", time: "4h ago", unread: true },
    { icon: "📄", text: "4 bills issued — $2,550.63 portfolio total", time: "1d ago", unread: true },
    { icon: "💳", text: "Batch payment $2,488.74 confirmed", time: "5d ago", unread: false },
  ],
};

function NotificationDropdown({ personaId, readIds, onMarkAllRead, onClose }: {
  personaId: string;
  readIds: Set<string>;
  onMarkAllRead: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const items = NOTIF_SEEDS[personaId] ?? NOTIF_SEEDS.P1 ?? [];
  const hasUnread = items.some((_, i) => !readIds.has(`${personaId}-${i}`));

  return (
    <div
      className="absolute right-0 mt-2 w-80 rounded-lg border border-neutral-200 bg-white shadow-lg py-2 z-50 animate-fade-in"
      role="menu"
    >
      <div className="px-4 py-2 border-b border-neutral-200 flex items-center justify-between">
        {/* These two were hardcoded English on shared chrome that renders on
            every authenticated route. The keys already existed — mobile's
            notification sheet has consumed them since Phase 2 — so web was the
            only surface telling a Spanish or Kreyòl reader "Notifications".
            Zero new keys; the shared value is "Mark all as read", which is what
            mobile has always said. */}
        <p className="text-sm font-semibold text-neutral-900">{t("notifications.title")}</p>
        {hasUnread && (
          <button
            onClick={onMarkAllRead}
            /* No aria-label: "Mark all notifications as read" did not contain
               the visible "Mark all as read" contiguously (WCAG 2.5.3). The
               menu's "Notifications" heading already supplies the context the
               longer name was reaching for. */
            className="text-xs text-brand-primary-text hover:underline py-1 -my-1"
          >
            {t("notifications.markAll")}
          </button>
        )}
      </div>
      {items.map((item, i) => {
        const isUnread = item.unread && !readIds.has(`${personaId}-${i}`);
        return (
          <button
            key={i}
            onClick={onClose}
            className={[
              "w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-neutral-50 transition-colors duration-fast",
              isUnread ? "bg-brand-primary/5" : "",
            ].join(" ")}
            role="menuitem"
          >
            <span className="text-base leading-none mt-0.5" aria-hidden>{item.icon}</span>
            <div className="flex-1 min-w-0">
              <p className={`text-sm ${isUnread ? "font-semibold text-neutral-900" : "text-neutral-700"}`}>
                {item.text}
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">{item.time}</p>
            </div>
            {isUnread && (
              <span className="w-2 h-2 rounded-full bg-brand-primary flex-shrink-0 mt-1.5" />
            )}
          </button>
        );
      })}
      <div className="px-4 py-2 border-t border-neutral-200">
        <p className="text-xs text-neutral-500 text-center">{t("notifications.allShown")}</p>
      </div>
    </div>
  );
}
